import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import { useSEO, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function OffrePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'Offre et tarifs - THE 21-DAY BUSINESS WEBSITE™',
        'Découvrez notre offre complète : site web business professionnel en 3 semaines pour 5 500 € + maintenance 100 €/mois.',
        `${siteUrl}/offre`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Offre', url: '/offre' }
      ]),
      {
        '@type': 'Product',
        name: 'THE 21-DAY BUSINESS WEBSITE™',
        description: 'Site web business complet en 3 semaines',
        offers: {
          '@type': 'Offer',
          price: '5500',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          priceValidUntil: '2025-12-31',
          itemCondition: 'https://schema.org/NewCondition'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '50'
        }
      }
    ]
  };

  useSEO({
    title: 'Offre et tarifs - Site web business en 3 semaines | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Découvrez notre offre complète : site web business professionnel en 3 semaines pour 5 500 € + maintenance 100 €/mois. Design premium, SEO et garantie satisfaction.',
    keywords: 'tarif site web, prix création site, offre site business, devis site web, agence web tarifs',
    canonical: `${siteUrl}/offre`,
    schema
  });

  const included = [
    { icon: 'ri-lightbulb-line', text: 'Stratégie digitale complète' },
    { icon: 'ri-palette-line', text: 'Design premium sur mesure' },
    { icon: 'ri-file-text-line', text: '15 pages professionnelles rédigées' },
    { icon: 'ri-image-line', text: 'Visuels optimisés et retravaillés' },
    { icon: 'ri-search-line', text: 'SEO technique complet' },
    { icon: 'ri-global-line', text: 'Nom de domaine 1 an' },
    { icon: 'ri-server-line', text: 'Hébergement premium 1 an' },
    { icon: 'ri-mail-line', text: 'Email professionnel' },
    { icon: 'ri-customer-service-line', text: 'Support 30 jours post-lancement' }
  ];

  const results = [
    'Un site business professionnel qui inspire confiance',
    'Une présence en ligne crédible et moderne',
    'Des pages optimisées pour la conversion',
    'Un positionnement clair et différenciant',
    'Une visibilité améliorée sur Google',
    'Un outil de génération de leads 24/7'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-600/20 text-yellow-500 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <i className="ri-vip-crown-line"></i>
              <span>Offre premium</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              THE 21-DAY<br />
              <span className="text-yellow-500">BUSINESS WEBSITE™</span>
            </h1>
            <p className="text-2xl text-slate-300 mb-8">
              Votre site business complet en 3 semaines
            </p>
            <div className="text-6xl font-bold text-yellow-500 mb-4">
              5 500 €
            </div>
            <p className="text-xl text-slate-400">
              + 100 €/mois (maintenance + 2 pages révisées)
            </p>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Ce qui est inclus
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Un package complet pour lancer votre présence en ligne professionnelle
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {included.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-4 bg-slate-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-xl text-white`}></i>
                  </div>
                  <span className="text-slate-900 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-white p-12 rounded-3xl border-2 border-yellow-200">
              <div className="text-center mb-8">
                <i className="ri-shield-star-line text-5xl text-yellow-700 mb-4"></i>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">Notre garantie</h3>
                <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
                  <strong>On refait le design 2 fois, et les contenus autant de fois qu'il le faut, jusqu'à ce que vous soyez satisfait.</strong>
                </p>
                <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
                  Pas de demi-mesure chez nous. Le site ne sort pas si vous n'êtes pas 100% satisfait !
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6">
                  Le résultat
                </h2>
                <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                  Un site business professionnel, crédible, optimisé pour la conversion.
                </p>
                
                <div className="space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-yellow-600 flex-shrink-0 mt-1"></i>
                      <span className="text-slate-700 text-lg">{result}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/69225f8aa28127c3abbd931a3036e6fa.jpg"
                    alt="Résultat site web"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Details */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="bg-slate-900 text-white p-12 rounded-3xl">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-6">Investissement</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-slate-800 p-8 rounded-2xl">
                  <div className="text-yellow-500 font-semibold mb-2">Création</div>
                  <div className="text-5xl font-bold mb-4">5 500 €</div>
                  <p className="text-slate-400">
                    Paiement unique pour la création complète de votre site business
                  </p>
                </div>

                <div className="bg-slate-800 p-8 rounded-2xl">
                  <div className="text-yellow-500 font-semibold mb-2">Maintenance</div>
                  <div className="text-5xl font-bold mb-4">100 €<span className="text-2xl">/mois</span></div>
                  <p className="text-slate-400">
                    Maintenance technique + 2 pages révisées par mois
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-yellow-600/10 border border-yellow-600/30 rounded-xl">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-2xl text-yellow-500 flex-shrink-0"></i>
                  <p className="text-slate-300">
                    <strong className="text-white">Paiement en plusieurs fois possible.</strong> Contactez-nous pour discuter d'un plan de paiement adapté à votre situation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gradient-to-br from-yellow-600 to-yellow-700 text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à transformer votre présence en ligne ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Démarrez votre projet aujourd'hui et ayez votre site en ligne dans 3 semaines
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button variant="secondary" size="lg">
                  Démarrer mon projet
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-yellow-700">
                  Voir le processus
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
